import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface RobotDetectionResult {
  isRobot: boolean;
  confidence: number; // 0-1, where 1 is definitely a robot
  reasons: string[];
  score: number;
}

export interface UserInteraction {
  timestamp: number;
  type: 'mouseMove' | 'click' | 'keypress' | 'focus' | 'blur';
  target?: string;
  x?: number;
  y?: number;
}

@Injectable({
  providedIn: 'root',
})
export class RobotDetectionService {
  private interactions: UserInteraction[] = [];
  private formStartTime = 0;
  private mouseMovements: { x: number; y: number; timestamp: number }[] = [];
  private keypressCount = 0;
  private focusEvents = 0;
  private recaptchaScore: number | null = null;

  private detectionResult = new BehaviorSubject<RobotDetectionResult>({
    isRobot: false,
    confidence: 0,
    reasons: [],
    score: 0,
  });

  public detectionResult$ = this.detectionResult.asObservable();

  /**
   * Initialize robot detection for a form
   */
  public initializeDetection(): void {
    this.formStartTime = Date.now();
    this.interactions = [];
    this.mouseMovements = [];
    this.keypressCount = 0;
    this.focusEvents = 0;
    this.recaptchaScore = null;
    this.updateDetectionResult();
  }

  /**
   * Track user interaction
   */
  public trackInteraction(interaction: UserInteraction): void {
    this.interactions.push(interaction);

    switch (interaction.type) {
      case 'mouseMove':
        if (interaction.x !== undefined && interaction.y !== undefined) {
          this.mouseMovements.push({
            x: interaction.x,
            y: interaction.y,
            timestamp: interaction.timestamp,
          });
        }
        break;
      case 'keypress':
        this.keypressCount += 1;
        break;
      case 'focus':
        this.focusEvents += 1;
        break;
    }

    this.updateDetectionResult();
  }

  /**
   * Set reCAPTCHA score (from reCAPTCHA v3)
   */
  public setRecaptchaScore(score: number): void {
    this.recaptchaScore = score;
    this.updateDetectionResult();
  }

  /**
   * Check if honeypot fields are filled
   */
  public checkHoneypot(honeypotValues: { [key: string]: any }): boolean {
    return Object.values(honeypotValues).some((value) => value && value.toString().trim() !== '');
  }

  /**
   * Analyze form submission timing
   */
  private analyzeTimingPatterns(): { suspicious: boolean; reason?: string } {
    const submissionTime = Date.now();
    const timeTaken = submissionTime - this.formStartTime;

    // Too fast (less than 5 seconds for a registration form)
    if (timeTaken < 5000) {
      return { suspicious: true, reason: 'Form submitted too quickly' };
    }

    // Suspiciously consistent timing patterns
    const interactionIntervals = this.interactions
      .slice(1)
      .map((interaction, index) => interaction.timestamp - this.interactions[index].timestamp);

    if (interactionIntervals.length > 5) {
      const avgInterval = interactionIntervals.reduce((a, b) => a + b, 0) / interactionIntervals.length;
      const variance =
        interactionIntervals.reduce((acc, interval) => acc + Math.pow(interval - avgInterval, 2), 0) / interactionIntervals.length;

      // Very low variance suggests robotic behavior
      if (variance < 100 && avgInterval < 200) {
        return { suspicious: true, reason: 'Suspiciously consistent interaction timing' };
      }
    }

    return { suspicious: false };
  }

  /**
   * Analyze mouse movement patterns
   */
  private analyzeMouseMovements(): { suspicious: boolean; reason?: string } {
    if (this.mouseMovements.length < 10) {
      return { suspicious: true, reason: 'Insufficient mouse movement' };
    }

    // Check for perfectly straight lines (robotic movement)
    let straightLineCount = 0;
    for (let i = 2; i < this.mouseMovements.length; i++) {
      const prev = this.mouseMovements[i - 2];
      const curr = this.mouseMovements[i - 1];
      const next = this.mouseMovements[i];

      const slope1 = (curr.y - prev.y) / (curr.x - prev.x);
      const slope2 = (next.y - curr.y) / (next.x - curr.x);

      if (Math.abs(slope1 - slope2) < 0.01) {
        straightLineCount += 1;
      }
    }

    if (straightLineCount > this.mouseMovements.length * 0.8) {
      return { suspicious: true, reason: 'Robotic mouse movement patterns' };
    }

    // Check for unnatural speed consistency
    const speeds = this.mouseMovements.slice(1).map((movement, index) => {
      const prev = this.mouseMovements[index];
      const distance = Math.sqrt(Math.pow(movement.x - prev.x, 2) + Math.pow(movement.y - prev.y, 2));
      const time = movement.timestamp - prev.timestamp;
      return time > 0 ? distance / time : 0;
    });

    if (speeds.length > 5) {
      const avgSpeed = speeds.reduce((a, b) => a + b, 0) / speeds.length;
      const speedVariance = speeds.reduce((acc, speed) => acc + Math.pow(speed - avgSpeed, 2), 0) / speeds.length;

      if (speedVariance < 0.1 && avgSpeed > 0) {
        return { suspicious: true, reason: 'Unnatural mouse speed consistency' };
      }
    }

    return { suspicious: false };
  }

  /**
   * Analyze interaction patterns
   */
  private analyzeInteractionPatterns(): { suspicious: boolean; reason?: string } {
    // Check for lack of focus events (robots often don't trigger focus)
    if (this.focusEvents === 0 && this.interactions.length > 5) {
      return { suspicious: true, reason: 'No focus events detected' };
    }

    // Check for lack of keypress events in text fields
    if (this.keypressCount === 0 && this.interactions.length > 3) {
      return { suspicious: true, reason: 'No keyboard interaction detected' };
    }

    // Check for unnatural interaction sequence
    const clicksBeforeAnyInput = this.interactions.findIndex((i) => i.type === 'keypress' || i.type === 'focus');
    const totalClicks = this.interactions.filter((i) => i.type === 'click').length;

    if (clicksBeforeAnyInput > 5 && totalClicks > 10) {
      return { suspicious: true, reason: 'Unusual click patterns before input' };
    }

    return { suspicious: false };
  }

  /**
   * Update the overall detection result
   */
  private updateDetectionResult(): void {
    const reasons: string[] = [];
    let suspiciousScore = 0;

    // Analyze timing patterns
    const timingAnalysis = this.analyzeTimingPatterns();
    if (timingAnalysis.suspicious && timingAnalysis.reason) {
      reasons.push(timingAnalysis.reason);
      suspiciousScore += 0.3;
    }

    // Analyze mouse movements
    const mouseAnalysis = this.analyzeMouseMovements();
    if (mouseAnalysis.suspicious && mouseAnalysis.reason) {
      reasons.push(mouseAnalysis.reason);
      suspiciousScore += 0.25;
    }

    // Analyze interaction patterns
    const interactionAnalysis = this.analyzeInteractionPatterns();
    if (interactionAnalysis.suspicious && interactionAnalysis.reason) {
      reasons.push(interactionAnalysis.reason);
      suspiciousScore += 0.2;
    }

    // Factor in reCAPTCHA score if available
    if (this.recaptchaScore !== null) {
      if (this.recaptchaScore < 0.3) {
        reasons.push('Low reCAPTCHA score');
        suspiciousScore += 0.4;
      } else if (this.recaptchaScore < 0.5) {
        reasons.push('Moderate reCAPTCHA score');
        suspiciousScore += 0.2;
      }
    }

    const confidence = Math.min(suspiciousScore, 1);
    // Check if running on localhost - be more lenient with robot detection
    const hostname = window.location.hostname;
    const isLocalhost = hostname.includes('localhost') || hostname.includes('127.0.0.1');
    const robotThreshold = isLocalhost ? 0.9 : 0.6;
    const isRobot = confidence > robotThreshold;

    this.detectionResult.next({
      isRobot,
      confidence,
      reasons,
      score: this.recaptchaScore || 0,
    });
  }

  /**
   * Get current detection result
   */
  public getCurrentResult(): RobotDetectionResult {
    return this.detectionResult.value;
  }

  /**
   * Reset detection state
   */
  public reset(): void {
    this.initializeDetection();
  }
}
